import { useEffect, useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { BadgeCheck, Building2, ExternalLink, Eye, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getAgenciaPublic } from "@/lib/agencias.functions";
import { listAnunciosPublicos } from "@/lib/anuncios.functions";
import { Card, CardContent } from "@/components/ui/card";
import { formatPrice } from "@/lib/utils";

export const Route = createFileRoute("/agencia-screenshot/$id")({
  head: () => ({
    meta: [
      { title: "Screenshot de agencia — Admin Bizaclia" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => {
    const raw = search.anuncios;
    let anuncios: number | "all" = 1;
    if (typeof raw === "string") {
      if (raw === "all") anuncios = "all";
      else {
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n > 0) anuncios = n;
      }
    } else if (typeof raw === "number" && raw > 0) {
      anuncios = Math.floor(raw);
    }
    const screenshot = search.screenshot === "1" || search.screenshot === 1 || search.screenshot === true;
    return { anuncios, screenshot };
  },
  component: AgenciaScreenshotPage,
  errorComponent: ({ error }) => (
    <main className="container mx-auto max-w-3xl px-4 py-16">
      <p className="text-destructive">{error.message}</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="container mx-auto max-w-3xl px-4 py-16">
      <p className="text-muted-foreground">Agencia no encontrada.</p>
    </main>
  ),
});

type AdminGate =
  | { status: "loading" }
  | { status: "denied"; reason: string }
  | { status: "ok" };

function useAdminOnly(): AdminGate {
  const [state, setState] = useState<AdminGate>({ status: "loading" });
  useEffect(() => {
    let active = true;
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const user = userRes.user;
      if (!user) {
        if (active) setState({ status: "denied", reason: "Debes iniciar sesión como admin." });
        return;
      }
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!active) return;
      if (error || !data) {
        setState({ status: "denied", reason: "Solo los admins de Bizaclia pueden ver esta página." });
      } else {
        setState({ status: "ok" });
      }
    })();
    return () => {
      active = false;
    };
  }, []);
  return state;
}

function AgenciaScreenshotPage() {
  const { id } = Route.useParams();
  const { anuncios: anunciosLimit, screenshot: screenshotMode } = Route.useSearch();
  const adminGate = useAdminOnly();
  // En modo screenshot (?screenshot=1) saltamos la comprobación de admin:
  // la página muestra solo datos públicos y se usa para generar la captura
  // que se incrusta en emails.
  const gate: AdminGate = screenshotMode ? { status: "ok" } : adminGate;

  const getAg = useServerFn(getAgenciaPublic);
  const listAn = useServerFn(listAnunciosPublicos);

  const qAg = useQuery({
    queryKey: ["screenshot-agencia", id],
    queryFn: async () => {
      const a = await getAg({ data: { id } });
      if (!a) throw notFound();
      return a;
    },
    enabled: gate.status === "ok",
  });

  const qAn = useQuery({
    queryKey: ["screenshot-agencia-anuncios", id],
    queryFn: () =>
      listAn({
        data: { agencia_ids: [id], limit: 100, offset: 0, with_count: true },
      }),
    enabled: gate.status === "ok" && !!qAg.data,
  });

  if (gate.status === "loading") {
    return (
      <main className="container mx-auto max-w-7xl px-4 py-10">
        <p className="text-muted-foreground">Verificando permisos…</p>
      </main>
    );
  }
  if (gate.status === "denied") {
    return (
      <main className="container mx-auto max-w-3xl px-4 py-16 text-center space-y-3">
        <h1 className="font-display text-2xl font-semibold">Acceso restringido</h1>
        <p className="text-muted-foreground">{gate.reason}</p>
      </main>
    );
  }

  if (qAg.isLoading) {
    return (
      <main className="container mx-auto max-w-7xl px-4 py-10">
        <p className="text-muted-foreground">Cargando…</p>
      </main>
    );
  }
  if (!qAg.data) return null;

  const a = qAg.data;
  const allItems = qAn.data?.items ?? [];
  const items =
    anunciosLimit === "all" ? allItems : allItems.slice(0, anunciosLimit);

  const ready = !qAn.isLoading;
  return (
    <main
      className="container mx-auto max-w-7xl px-4 sm:px-6 py-4 sm:py-6"
      data-screenshot-ready={ready ? "1" : undefined}
    >
      <section className="flex flex-row gap-4 sm:gap-6 items-start">
        <div className="h-16 w-16 sm:h-24 sm:w-24 shrink-0 rounded-xl bg-muted overflow-hidden flex items-center justify-center border">
          {a.logo_url ? (
            <img src={a.logo_url} alt={`Logo de ${a.nombre}`} className="h-full w-full object-contain" />
          ) : (
            <Building2 className="h-8 w-8 sm:h-10 sm:w-10 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
              {a.nombre.length > 15 ? a.nombre.slice(0, 15) + "..." : a.nombre}
            </h1>
            {a.estado === "activa" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                <BadgeCheck className="h-4 w-4" />
                Verificada
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {a.num_empleados != null && (
              <span className="inline-flex items-center gap-1">
                <Users className="h-4 w-4" /> {a.num_empleados} personas
              </span>
            )}
            {a.url_web && (
              <a
                href={a.url_web}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" /> Web
              </a>
            )}
          </div>
          {!screenshotMode && a.descripcion_publica && (
            <p className="mt-4 text-muted-foreground max-w-3xl whitespace-pre-line">
              {a.descripcion_publica}
            </p>
          )}
        </div>
      </section>

      <div className="mt-4 flex flex-col gap-4">
        {qAn.isLoading ? (
          <p className="text-muted-foreground">Cargando anuncios…</p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground">Esta agencia aún no tiene anuncios publicados.</p>
        ) : (
          items.map((row: any) => (
            <AnuncioCard key={row.id} row={row} />
          ))
        )}
      </div>
    </main>
  );
}

// Mismo diseño que el listado del portal (src/components/portal/PortalPage.tsx)
function AnuncioCard({ row }: { row: any }) {
  const op = (row.operacion ?? "").toLowerCase();

  return (
    <Card className="w-full h-full">
      <div className="flex flex-col sm:flex-row h-full sm:min-h-[230px] overflow-hidden rounded-xl shadow-sm">
        <Link
          to="/local/$id"
          params={{ id: row.id }}
          search={{} as never}
          className="relative sm:w-72 sm:shrink-0 h-48 sm:h-full bg-muted overflow-hidden block"
          aria-label={`Ver detalles de ${row.usos_local?.[0] ?? row.tipo_local ?? "Local"}`}
        >
          {row.cover_url ? (
            <img
              src={row.cover_url}
              alt={`${row.usos_local?.[0] ?? row.tipo_local ?? "Local"}${row.operacion ? ` en ${row.operacion}` : ""}${row.ciudad ? ` en ${row.barrio ? `${row.barrio}, ${row.ciudad}` : row.ciudad}` : ""}`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          {(row.vistas_total ?? 0) > 0 && (
            <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md bg-background/85 backdrop-blur px-1.5 py-0.5 text-[11px] text-muted-foreground">
              <Eye className="h-3 w-3" />
              {row.vistas_total}
            </div>
          )}
        </Link>
        <CardContent className="flex-1 min-w-0 p-4 space-y-2 flex flex-col h-full">
          <div className="min-w-0 space-y-1">
            <h3 className="text-lg font-semibold break-words">
              <Link to="/local/$id" params={{ id: row.id }} search={{} as never} className="hover:text-primary transition-colors">
                {row.usos_local?.[0] ?? row.tipo_local ?? "Local"}
                {row.operacion ? ` en ${row.operacion}` : ""}
                {row.barrio ? ` en ${row.barrio}, ${row.ciudad}` : row.ciudad ? ` en ${row.ciudad}` : ""}
              </Link>
            </h3>

            <PriceBlock row={row} op={op} />

            {/* Dirección oculta en la captura para email */}
          </div>

          {row.descripcion && (
            <p className="text-sm text-muted-foreground line-clamp-2">{row.descripcion}</p>
          )}

          <Features row={row} op={op} />
        </CardContent>
      </div>
    </Card>
  );
}

function PriceBlock({ row, op }: { row: any; op: string }) {
  const priceTag = (amount: number, label: string) => (
    <span>
      {formatPrice(amount)} € <span className="text-xs font-normal text-muted-foreground">/ {label}</span>
    </span>
  );

  if (op === "traspaso") {
    return (
      <div className="flex flex-wrap gap-x-2 items-baseline text-lg font-bold shrink-0">
        {row.precio_traspaso != null && priceTag(row.precio_traspaso, "Traspaso")}
        {row.precio_alquiler != null && priceTag(row.precio_alquiler, "Alquiler")}
        {row.precio_traspaso == null && row.precio_alquiler == null && <span>Consultar</span>}
      </div>
    );
  }
  if (op === "venta") {
    return (
      <div className="text-lg font-bold shrink-0">
        {row.precio_venta != null ? `${formatPrice(row.precio_venta)} €` : "Consultar"}
        {row.precio_venta != null && (
          <span className="text-xs font-normal text-muted-foreground"> / Venta</span>
        )}
      </div>
    );
  }
  if (op === "alquiler") {
    return (
      <div className="text-lg font-bold shrink-0">
        {row.precio_alquiler != null ? `${formatPrice(row.precio_alquiler)} €` : "Consultar"}
        {row.precio_alquiler != null && (
          <span className="text-xs font-normal text-muted-foreground"> / Alquiler</span>
        )}
      </div>
    );
  }
  if (op === "rentabilidad") {
    return (
      <div className="flex flex-wrap gap-x-2 items-baseline text-lg font-bold shrink-0">
        {row.precio_venta != null && priceTag(row.precio_venta, "Venta")}
        {row.rent_bruta != null && (
          <span className="text-sm font-normal text-muted-foreground">
            {row.rent_bruta} % Rentabilidad
          </span>
        )}
        {row.precio_venta == null && row.rent_bruta == null && <span>Consultar</span>}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-x-2 items-baseline text-lg font-bold shrink-0">
      {row.precio_alquiler != null && priceTag(row.precio_alquiler, "Alquiler")}
      {row.precio_traspaso != null && priceTag(row.precio_traspaso, "Traspaso")}
      {row.precio_venta != null && priceTag(row.precio_venta, "Venta")}
      {row.precio_alquiler == null && row.precio_traspaso == null && row.precio_venta == null && (
        <span>Consultar</span>
      )}
    </div>
  );
}

function Features({ row, op }: { row: any; op: string }) {
  const precioBase =
    op === "traspaso"
      ? (row.precio_traspaso ?? row.precio_alquiler ?? row.precio_venta)
      : op === "venta"
        ? (row.precio_venta ?? row.precio_alquiler ?? row.precio_traspaso)
        : (row.precio_alquiler ?? row.precio_venta ?? row.precio_traspaso);
  const precioM2 =
    row.t_superficie && row.t_superficie > 0 && precioBase != null
      ? precioBase / row.t_superficie
      : null;

  const items: string[] = [];
  if (row.t_superficie != null) items.push(`${row.t_superficie} m²`);
  if (precioM2 != null) items.push(`${formatPrice(precioM2)} €/m²`);
  if (row.nivel_transito)
    items.push(`Tráfico ${row.nivel_transito.toLowerCase().replace(/_/g, " ")}`);
  if (row.t_esquinero) items.push("Esquinero");
  if (row.h_salida_humos) items.push("Salida de humos");
  if (row.t_parking)
    items.push(row.t_parking_plazas ? `Parking (${row.t_parking_plazas})` : "Parking");
  if (row.h_terraza) items.push("Terraza");
  if (row.t_amueblado) items.push("Amueblado");

  const hasAgencia = row.agencia_id && (row.agencia_logo_url || row.agencia_nombre);

  return (
    <div className="flex items-end justify-between gap-3 pt-1 sm:mt-auto">
      <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
        {items.map((label) => (
          <span
            key={label}
            className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
          >
            {label}
          </span>
        ))}
      </div>
      <div className="shrink-0 flex items-end justify-center h-20 w-28 overflow-hidden">
        {hasAgencia ? (
          row.agencia_logo_url ? (
            <img
              src={row.agencia_logo_url}
              alt={`Logo de ${row.agencia_nombre ?? "la agencia inmobiliaria"}`}
              loading="lazy"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="px-1 text-[10px] text-center text-muted-foreground line-clamp-2">
              {row.agencia_nombre}
            </span>
          )
        ) : null}
      </div>
    </div>
  );
}
